import z from 'zod';
import { SectionIdSchema } from '~/types/sectionId';
import { LOCALE_PREFIX_REGEX } from '~/utils/localePattern';

/**
 * Maximum length of one stored path with query.
 *
 * The AWS WAF managed rule `SizeRestrictions_Cookie_HEADER` blocks a request when its `Cookie` header is longer
 * than 10,240 bytes (see `opentofu/waf.tf` in the repository `hfp-infrastructure`). The band cookie shares this
 * budget with the OAuth, Docebo, Shopware and consent cookies. Thus the cookie must stay small. The limit of
 * 4096 bytes per cookie in a browser is not the limiting value.
 *
 * Nuxt `useCookie` writes `<name>=` and `encodeURIComponent(JSON.stringify(state))`. The worst case is:
 *
 * - The path schema allows only characters that are not escaped in JSON and that
 *   `encodeURIComponent` writes with 3 bytes or less (`%XX`). The characters `"` and `\` need 6 bytes
 *   and the schema does not allow them.
 * - Two paths with 256 characters: 2 × 256 × 3 = 1536 bytes.
 * - The encoded JSON structure with the longest area names and the longest locale segment, and with
 *   empty paths: 192 bytes.
 * - The cookie name and the `=` character: 17 bytes.
 * - Total: 1536 + 192 + 17 = 1745 bytes. This is 17 % of the limit of the WAF.
 *
 * The test in `areaSwitchBand.test.ts` calculates the worst case again.
 */
export const AREA_SWITCH_BAND_MAX_PATH_LENGTH = 256;

/** The length of the JSON of a state with the longest area names, the longest locale segment and empty paths. */
const STATE_JSON_STRUCTURE_LENGTH = 110;

/**
 * Maximum length of the JSON in the cookie: the structure and two paths. The parser rejects a longer value
 * before `JSON.parse` reads it, because the cookie is user input.
 */
export const AREA_SWITCH_BAND_MAX_JSON_LENGTH = STATE_JSON_STRUCTURE_LENGTH + 2 * AREA_SWITCH_BAND_MAX_PATH_LENGTH;

/**
 * A character that Vue Router writes in `fullPath` (without the hash), or a valid percent escape.
 * The characters are the printable ASCII characters without space, `"`, `#`, `%`, `<`, `>` and `\`.
 * A `%` is valid only when two hexadecimal digits follow it.
 */
const PATH_CHARACTER = String.raw`[A-Za-z0-9\-._~!$&'()*+,;=:@[\]{}|^\x60]|%[0-9A-Fa-f]{2}`;

/** An empty string, or a path with an optional query. The path starts with exactly one `/` and has no empty segment. */
const STORED_PATH_PATTERN = new RegExp(
    String.raw`^(?:\/(?:(?:${PATH_CHARACTER})+(?:\/(?:${PATH_CHARACTER})+)*\/?)?(?:\?(?:${PATH_CHARACTER}|[/?])*)?)?$`
);

/** Empty string: no path stored. The back link then goes to the start page of the area. */
export const AreaSwitchBandPathSchema = z.string().max(AREA_SWITCH_BAND_MAX_PATH_LENGTH).regex(STORED_PATH_PATTERN);

export const AreaSwitchBandStateSchema = z
    .object({
        /** The last page view. */
        current: z.object({
            area: SectionIdSchema,
            localeSegment: z.string().regex(LOCALE_PREFIX_REGEX),
            path: AreaSwitchBandPathSchema
        }),
        /** The area to go back to and its last page. `null` when no band is visible. */
        band: z
            .object({
                area: SectionIdSchema,
                path: AreaSwitchBandPathSchema
            })
            .nullable()
    })
    .refine(({ current, band }) => band?.area !== current.area, {
        message: 'The band must not point to the current area.'
    });

export type AreaSwitchBandState = z.infer<typeof AreaSwitchBandStateSchema>;

/** Treats every invalid value, such as a malformed or edited cookie, as "no state". */
export function parseAreaSwitchBandState(json: string | null | undefined): AreaSwitchBandState | null {
    if (!json || json.length > AREA_SWITCH_BAND_MAX_JSON_LENGTH) return null;

    let value: unknown;
    try {
        value = JSON.parse(json);
    } catch {
        return null;
    }

    const result = AreaSwitchBandStateSchema.safeParse(value);
    return result.success ? result.data : null;
}
